import { useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import '@fontsource/rubik';

import AuthLayout from '../layouts/AuthLayout';
import CustomerDashboardNavbar from '../customerdashboard/components/CustomerDashboardNavbar';
import useCredits from './hooks/useCredits';
import WalletSummary from './components/WalletSummary';
import ProductCard from './components/ProductCard';
import ConfirmDialog from './components/ConfirmDialog';
import { bodySx, captionSx, cardSx, DANGER, INK, MUTED, outlinedButtonSx, SOFT } from './components/styles';
import { pageState, productsInPayload } from './utils';

// Centred message card for the error and no-deal states.
function StateCard({ icon: Icon, tone = INK, title, children, testId, role }) {
  return (
    <Box sx={{ ...cardSx, textAlign: 'center', py: { xs: 5, md: 7 } }} data-testid={testId} role={role}>
      <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: SOFT, display: 'grid', placeItems: 'center', mx: 'auto', mb: 2 }}>
        <Icon sx={{ fontSize: 24, color: tone }} />
      </Box>
      {title && (
        <Typography sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: '16px', color: INK, mb: 0.75 }}>{title}</Typography>
      )}
      <Typography sx={{ ...bodySx, color: tone === DANGER ? DANGER : MUTED, maxWidth: 440, mx: 'auto' }}>{children}</Typography>
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }} data-testid="credits-loading" aria-busy="true">
      <Box sx={cardSx}>
        <Skeleton width={120} height={16} />
        <Skeleton width={220} height={30} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Skeleton variant="rounded" height={118} />
          <Skeleton variant="rounded" height={118} />
        </Box>
      </Box>
      {[0, 1].map((i) => (
        <Box key={i} sx={cardSx}>
          <Skeleton width={180} height={30} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={160} />
        </Box>
      ))}
    </Box>
  );
}

// Account-owner Credits page: the organisation's wallet, how it is shared
// across users and teams, and who holds a seat on each product.
const Credits = (props) => {
  const commonReducer = useSelector((state) => state.root.commonReducer);
  const isOwner = !!commonReducer?.is_account_owner;
  const { data, isLoading, isFetching, isError, errorMessage, refetch, run, pending } = useCredits();
  const [confirm, setConfirm] = useState(null);

  const state = pageState({ isOwner, isLoading, isError, data });
  if (state === 'redirect') return <Navigate to="/dashboard" replace />;

  return (
    <AuthLayout uploadProps={props.uploadProps}>
      <CustomerDashboardNavbar />
      <Box
        id="credits-content"
        sx={{
          px: { xs: 2, sm: 3, md: '40px', lg: '56px' },
          pt: { xs: 3, md: '26px' },
          pb: { xs: 8, md: '60px' },
          fontFamily: 'Rubik',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, lg: 2.5 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: { xs: 0.5, lg: 1 } }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: 'Rubik',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                fontSize: { xs: '24px', md: '26px', lg: '30px', xl: '36px' },
                color: INK,
              }}
            >
              Credits
            </Typography>
            <Typography sx={{ ...captionSx, mt: 0.75, maxWidth: 560 }}>
              Decide how your organisation's credits are shared and who has a seat on each product. Figures can lag real usage by up to a minute.
            </Typography>
          </Box>
          {state !== 'loading' && (
            <Button
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={
                <RefreshRoundedIcon
                  sx={{
                    animation: isFetching ? 'credits-spin 0.9s linear infinite' : 'none',
                    '@keyframes credits-spin': { to: { transform: 'rotate(360deg)' } },
                  }}
                />
              }
              sx={{ ...outlinedButtonSx, flexShrink: 0 }}
            >
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          )}
        </Box>

        {state === 'loading' && <LoadingSkeleton />}

        {state === 'error' && (
          <StateCard icon={ErrorOutlineIcon} tone={DANGER} title="We couldn't load your credits" testId="credits-error" role="alert">
            {errorMessage}
          </StateCard>
        )}

        {state === 'no_deal' && (
          <StateCard icon={ReceiptLongOutlinedIcon} title="No active contract" testId="credits-no-deal">
            {data?.organisation?.name} has no current deal, so there are no credits to manage yet.
          </StateCard>
        )}

        {(state === 'unmanaged' || state === 'managed') && (
          <WalletSummary data={data} minutesPerCredit={commonReducer?.credits_minutes_per_credit} />
        )}

        {state === 'managed' &&
          productsInPayload(data).map(({ key, label }) => (
            <ProductCard
              key={key}
              product={key}
              label={label}
              data={data}
              run={run}
              pending={pending}
              askConfirm={setConfirm}
            />
          ))}
      </Box>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </AuthLayout>
  );
};

export default Credits;
